import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import {
  viewPresentationSession,
  viewPresentation,
  viewPublicPresentation,
  submitAnswerToAPI,
  joinSession,
} from "../redux/features/presentations/presentationsSlice";
import { useAnimation } from "./AnimateTab/AnimationContext";
import { SlideCanvasRenderer } from "./SlideCanvasRenderer";
import SlideQuestionRenderer from "./SlideQuestionRenderer";

const Attendee = () => {
  const { uuid, sessionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [socket, setSocket] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [answerState, setAnswerState] = useState({
    text: "",
    pollOption: "",
    multipleOptions: [],
  });
  const onAnswerChange = (updates) =>
    setAnswerState((prev) => ({ ...prev, ...updates }));
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
  const [attendeeInfo, setAttendeeInfo] = useState(null);
  const [userSessionId, setUserSessionId] = useState(null);
  const [isEnded, setIsEnded] = useState(false);
  const {
    getPresentationSessionLoading,
    selectedPresentationSession,
    getPresentationSessionError,
    selectedPresentation,
  } = useSelector((state) => state.presentation);

  const { runGSAPPreview, setElements, setInteractions } = useAnimation();
  const runGSAPPreviewRef = useRef(runGSAPPreview);
  runGSAPPreviewRef.current = runGSAPPreview;

  const [attendeeId] = useState(() => {
    const storedId = localStorage.getItem("orvi_attendee_id");
    if (storedId) return storedId;

    const newId = Math.floor(Math.random() * 1000000000).toString();
    localStorage.setItem("orvi_attendee_id", newId);
    return newId;
  });

  useEffect(() => {
    const newSocket = io(
      process.env.REACT_APP_SOCKET_URL || "http://localhost:5000",
    );
    setSocket(newSocket);

    newSocket.on("connect", () => {
      newSocket.emit("joinPresentation", { uuid, sessionId });
    });

    newSocket.on("slideChange", (slideIndex) => {
      setCurrentSlide(slideIndex);
      setAnswerState({ text: "", pollOption: "", multipleOptions: [] });
    });
    newSocket.on("presentationEnd", () => {
      setIsEnded(true);
    });
    return () => {
      newSocket.disconnect();
    };
  }, [sessionId, navigate]);

  useEffect(() => {
    if (uuid) {
      const token = localStorage.getItem("OrviSlideToken");
      if (token) {
        dispatch(viewPresentation(uuid));
      } else {
        dispatch(viewPresentationSession(sessionId));
        // Always fetch full presentation to get animation_data (session API omits it)
        dispatch(viewPublicPresentation(uuid));
      }
    }
  }, [uuid, sessionId, dispatch]);

  useEffect(() => {
    // Use full presentation data (has animation_data) when available,
    // fall back to session data (which the session endpoint may return without animation_data)
    const slideSource = selectedPresentation || selectedPresentationSession;
    const fullSlides = selectedPresentation?.slides || [];

    if (slideSource && slideSource.slides) {
      const formattedSlides = slideSource.slides
        .filter((slide) => slide.is_active !== false)
        .map((slide) => {
          const slideType = slide.type || slide.type;

          // Prefer animation_data from selectedPresentation (session API may omit it)
          const fullSlide = fullSlides.find((s) => s.id === slide.id) || slide;
          const rawAnimData = fullSlide.animation_data ?? slide.animation_data;
          let parsedAnimationData = null;

          if (rawAnimData) {
            try {
              const parsed =
                typeof rawAnimData === "string"
                  ? JSON.parse(rawAnimData)
                  : rawAnimData;

              parsedAnimationData = {
                ...parsed,
                elements: (parsed?.elements || []).filter(Boolean),
                interactions: (parsed?.interactions || []).filter(Boolean),
              };
            } catch (err) {
              console.error("Failed to parse animation data", err);
            }
          }

          // QUESTION SLIDES
          if (slideType === "question" || (slide.question && !slide.image)) {
            return {
              id: slide.id,
              question_id: slide.question.id,
              type: slide.question.question_type,
              title: slide.title,
              question: slide.question.question_text,
              options: slide.question.choices || [],
              with_animation: slide.with_animation || false,
              animation_data: parsedAnimationData,
            };
          }

          // IMAGE SLIDES
          if ((slideType === "image" || slide.image) && slide.image) {
            let url = slide.image.image_url;

            if (url && !url.startsWith("http") && !url.startsWith("blob:")) {
              try {
                const apiUrl =
                  process.env.REACT_APP_API_URL || "http://localhost:49152";
                const origin = new URL(apiUrl).origin;

                url = `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
              } catch (e) {
                console.error("Error parsing API URL:", e);

                url = `${
                  process.env.REACT_APP_API_URL || "http://localhost:49152"
                }${url}`;
              }
            }

            return {
              id: slide.id,
              type: "image",
              title: slide.image.title || slide.title,
              imageUrl: url,
              with_animation: slide.with_animation || false,
              animation_data: parsedAnimationData,
            };
          }

          // BLANK SLIDES
          if (slideType === "blank") {
            return {
              id: slide.id,
              type: "blank",
              title: slide.title,
              canvasBackgroundColor:
                parsedAnimationData?.canvasBackgroundColor || "#1a1a2e",
              with_animation: slide.with_animation || false,
              animation_data: parsedAnimationData,
            };
          }

          // FALLBACK
          return {
            id: slide.id,
            type: slide.type,
            title: slide.title,
            question: slide.question,
            options: slide.options,
            imageUrl:
              slide.imageUrl ||
              (slide.file
                ? `${
                    process.env.REACT_APP_API_URL || "http://localhost:5000"
                  }/slides/${slide.file}`
                : null),
            with_animation: slide.with_animation || false,
            animation_data: parsedAnimationData,
          };
        });
      setSlides(formattedSlides);
    }

    if (uuid && sessionId && attendeeId && selectedPresentationSession?.id) {
      dispatch(
        joinSession({
          sessionId,
          presentationId: selectedPresentationSession.id, // Use numeric ID for backend
          attendeeId,
        }),
      )
        .unwrap()
        .then((data) => {
          setUserSessionId(data.user_session_id);
        })
        .catch((err) => console.error("Failed to join session:", err));
    }
  }, [
    dispatch,
    uuid,
    sessionId,
    selectedPresentationSession,
    selectedPresentation,
    attendeeId,
  ]);

  useEffect(() => {
    const slide = slides[currentSlide];
    if (!slide?.with_animation) return;

    // Every animated slide type (question / image / blank) runs through the
    // same canvas pipeline so animations play natively on positioned elements.
    const { elements: els = [], interactions: ints = [] } =
      slide.animation_data || {};

    setElements(els);
    setInteractions(ints.filter(Boolean));
    const timer = setTimeout(() => runGSAPPreviewRef.current(), 80);
    return () => clearTimeout(timer);
  }, [currentSlide, slides]);

  const slideData = slides[currentSlide];

  const handleSubmitAnswer = (e) => {
    e.preventDefault();
    if (socket && !submittedAnswers.has(currentSlide)) {
      const currentSlideData = slides[currentSlide];
      let answerData = {
        uuid, // Use UUID from URL for Socket room consistency
        sessionId,
        slideNumber: currentSlide,
      };

      const apiAnswerData = {
        question_id: currentSlideData?.question_id || currentSlideData?.id,
        user_session_id: userSessionId || sessionId, // Use UUID for user_session_id (verified)
        attendee_id: attendeeId,
        presentation_id: selectedPresentationSession?.id || uuid, // Legacy/Numeric ID for API if needed
      };

      if (slideData?.type === "text") {
        if (answerState.text.trim()) {
          answerData.answer = answerState.text.trim();
          answerData.answerType = "text";
          apiAnswerData.answer_value = answerState.text.trim();
        } else {
          return;
        }
      } else if (slideData?.type === "poll") {
        if (answerState.pollOption) {
          const choice = slideData.options.find(
            (o) => o.id === answerState.pollOption,
          );
          answerData.answer = choice?.choice_text || "";
          answerData.answerId = answerState.pollOption;
          answerData.answerType = "poll";
          apiAnswerData.answer_value = answerState.pollOption;
        } else {
          return;
        }
      } else if (slideData?.type === "multiple") {
        if (answerState.multipleOptions.length > 0) {
          const choiceTexts = slideData.options
            .filter((o) => answerState.multipleOptions.includes(o.id))
            .map((o) => o.choice_text);
          answerData.answer = choiceTexts;
          answerData.answerIds = answerState.multipleOptions;
          answerData.answerType = "multiple";
          apiAnswerData.answer_value = JSON.stringify(
            answerState.multipleOptions,
          );
        } else {
          return;
        }
      } else {
        return;
      }

      socket.emit("submitAnswer", answerData);

      dispatch(submitAnswerToAPI(apiAnswerData));

      const newSubmittedAnswers = new Set([...submittedAnswers, currentSlide]);
      setSubmittedAnswers(newSubmittedAnswers);

      setAnswerState({ text: "", pollOption: "", multipleOptions: [] });
    }
  };

  const canSubmitAnswer = () => {
    if (submittedAnswers.has(currentSlide)) return false;
    if (slideData?.type === "text") return answerState.text.trim().length > 0;
    if (slideData?.type === "poll") return !!answerState.pollOption;
    if (slideData?.type === "multiple")
      return answerState.multipleOptions.length > 0;
    return false;
  };

  if (isEnded) {
    return (
      <div className="h-screen w-screen flex flex-col justify-center items-center bg-gray-50 text-center p-6 font-primary">
        <div className="bg-white p-10 rounded-2xl shadow-xl max-w-md transform transition-all scale-100 flex flex-col items-center">
          <div className="w-20 h-20 bg-purple-50 text-purpleColor rounded-full flex items-center justify-center mb-6 text-3xl">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Presentation Ended
          </h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Thank you for participating! The speaker has ended this
            presentation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col min-h-screen lg:min-h-0">
        <div className="flex-1 flex justify-center items-center">
          {(slideData?.type === "image" || slideData?.type === "pdf") && (
            <div className="relative lg:h-[80vh] w-[95vw] lg:w-[65vw] overflow-hidden">
              <img
                src={slideData.imageUrl}
                alt={slideData.title}
                className="w-full h-full object-contain"
              />

              <SlideCanvasRenderer slide={slideData} />
            </div>
          )}

          {slideData?.type === "blank" && (
            <div className="relative h-[80vh] w-[65vw] overflow-hidden">
              <div
                className="w-full h-full"
                style={{
                  backgroundColor: slideData.canvasBackgroundColor || "#1a1a2e",
                }}
              />

              <SlideCanvasRenderer slide={slideData} />
            </div>
          )}
          {(slideData?.type === "poll" ||
            slideData?.type === "multiple" ||
            slideData?.type === "text") &&
            (() => {
              const hasCanvas =
                (slideData?.animation_data?.elements || []).length > 0;

              // Canvas layout — interactive positioned/animatable elements,
              // rendered like other slide types.
              if (hasCanvas) {
                const bgColor =
                  slideData.animation_data?.canvasBackgroundColor || "#f3f4f6";
                const bgImage = slideData.animation_data?.canvasBackground;
                return (
                  <div
                    className="relative h-[80vh] w-[95vw] lg:w-[65vw] aspect-video overflow-hidden rounded-lg"
                    style={{
                      backgroundColor: bgColor,
                      ...(bgImage
                        ? {
                            backgroundImage: `url("${bgImage}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : {}),
                    }}
                  >
                    <SlideCanvasRenderer
                      slide={slideData}
                      answerState={answerState}
                      onAnswerChange={onAnswerChange}
                      submittedAnswers={submittedAnswers}
                      currentSlide={currentSlide}
                    />
                    <button
                      onClick={handleSubmitAnswer}
                      disabled={!canSubmitAnswer()}
                      className="absolute bottom-[4%] right-[3%] z-10 px-6 py-2 bg-lightPurple text-white rounded-lg font-bold disabled:opacity-50 hover:bg-purple-700 transition text-sm"
                    >
                      {submittedAnswers.has(currentSlide)
                        ? "Answer Submitted"
                        : "Submit Answer"}
                    </button>
                  </div>
                );
              }

              // Fallback — question never opened in the editor (no canvas data).
              return (
                <SlideQuestionRenderer
                  slide={slideData}
                  currentSlide={currentSlide}
                  answerState={answerState}
                  onAnswerChange={onAnswerChange}
                  submittedAnswers={submittedAnswers}
                  handleSubmitAnswer={handleSubmitAnswer}
                  canSubmitAnswer={canSubmitAnswer}
                />
              );
            })()}

          {/* 🔄 Fallback states */}
          {getPresentationSessionLoading && (
            <p className="text-gray-500 text-center py-10 text-xl animate-pulse">
              Loading presentation details...
            </p>
          )}

          {getPresentationSessionError && (
            <div className="text-center py-10">
              <p className="text-red-500 text-xl font-bold mb-2">
                Unable to load presentation
              </p>
              <p className="text-gray-600">{getPresentationSessionError}</p>
              <p className="text-xs text-gray-400 mt-4">
                (Error: {getPresentationSessionError})
              </p>
              {!localStorage.getItem("OrviSlideToken") && (
                <p className="text-orange-500 mt-4 font-semibold">
                  Note: You are not logged in. This presentation might be
                  private.
                </p>
              )}
            </div>
          )}

          {!slideData &&
            !getPresentationSessionLoading &&
            !getPresentationSessionError && (
              <div className="flex flex-col items-center">
                <p className="text-gray-500 text-center py-10">
                  Waiting for presentation to start... (No slides loaded)
                </p>
                {selectedPresentationSession &&
                  !selectedPresentationSession.slides && (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded text-sm text-yellow-700 max-w-md">
                      <p className="font-bold">Debug Info:</p>
                      <p>
                        Presentation data was fetched, but no slides were found.
                      </p>
                      <p>
                        Please check if the backend API includes{" "}
                        <code>with('slides')</code>.
                      </p>
                    </div>
                  )}
              </div>
            )}
        </div>

        {/* ---------- Footer ---------- */}
        <div className=" border-t py-[2vw] px-[0.4vw]">
          <div className="flex justify-between flex-wrap gap-4 items-center">
            <div className="flex flex-col gap-2 w-[90vw]">
              <p className="text-[2.2vw] lg:text-[1vw] font-medium text-gray-500">
                As an attendee, you can view the presentation and submit answers
                to questions.
              </p>
              {/* {currentSlide === 0 && (
                <>
                  <p className="text-[2.2vw] lg:text-[0.7vw] font-medium text-gray-500">
                    This material is intended to be read/used by healthcare
                    professional, and not for distribution amongst general
                    public
                  </p>

                  <p className="text-[2.2vw] lg:text-[0.7vw] font-medium text-gray-500">
                    Breastfeeding is the best source of infant nutrition. Good
                    maternal nutrition is important for preparation and
                    maintenance of breastfeeding. When infant formula is to be
                    used, a mother should be aware of the financial and social
                    implications of formula feeding, the difficulty of reversing
                    the decision not to breastfeed, and the care that must be
                    taken to prevent partial formula feeding from interfering
                    with lactation. Proper preparation of powder or concentrated
                    liquid infant formula also requires the use of boiled water
                    to mix the formula and clean implements as well as careful
                    adherence to mixing instructions. Improper mixing or
                    preparation may make the baby sick. A health care
                    professional should be consulted before initiating formula
                    feeding.
                  </p>

                  <p className="text-[2.2vw] lg:text-[0.7vw] font-medium text-gray-500">
                    ANIEMEA-P-SA-202600087
                  </p>
                </>
              )} */}

              {attendeeInfo && (
                <div className="text-xs text-gray-400">
                  <span>Submitted: {submittedAnswers.size} answers | </span>
                  <span>
                    Joined:{" "}
                    {new Date(attendeeInfo.joinedAt).toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>

            <span className="text-gray-700 font-semibold text-[2.5vw] lg:text-[1.5vw]">
              {currentSlide + 1} / {slides.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attendee;
